import { useEffect, useRef, useState } from 'react'
import { cn } from '#app/utils/misc.tsx'

type Animation =
	| 'rise'
	| 'fade-up'
	| 'fade-in'
	| 'slide-left'
	| 'slide-right'
	| 'scale-in'

/** The motion language's entrance: its hidden state and its timing, on the
 * motion tokens. CardWave runs its cards on it too. */
export const rise = {
	initial: 'opacity-0 translate-y-6 blur-sm',
	timing: 'duration-medium ease-out-expo',
}

/** Where every entrance comes to rest. */
export const revealShown =
	'translate-x-0 translate-y-0 scale-100 opacity-100 blur-none'

// Each entrance's hidden state and its timing.
const legacyTiming = 'duration-500 ease-out'
const entrances: Record<Animation, { initial: string; timing: string }> = {
	rise,
	'fade-up': { initial: 'opacity-0 translate-y-5', timing: legacyTiming },
	'fade-in': { initial: 'opacity-0', timing: legacyTiming },
	'slide-left': { initial: 'opacity-0 -translate-x-5', timing: legacyTiming },
	'slide-right': { initial: 'opacity-0 translate-x-5', timing: legacyTiming },
	'scale-in': { initial: 'opacity-0 scale-95', timing: legacyTiming },
}

export const revealAnimations = Object.keys(entrances) as Animation[]

type RevealProps = {
	children: React.ReactNode
	className?: string
	animation?: Animation
	delay?: number
	threshold?: number
	once?: boolean
}

/** true once the element has come into view; at once with reduced motion. */
export function useInView<T extends Element>({
	threshold = 0.1,
	once = true,
}: { threshold?: number; once?: boolean } = {}) {
	const ref = useRef<T>(null)
	const [inView, setInView] = useState(false)

	useEffect(() => {
		const el = ref.current
		if (!el) return

		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			setInView(true)
			return
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry?.isIntersecting) {
					setInView(true)
					if (once) observer.disconnect()
				}
			},
			{ threshold },
		)

		observer.observe(el)
		return () => observer.disconnect()
	}, [threshold, once])

	return [ref, inView] as const
}

export function Reveal({
	children,
	animation = 'rise',
	delay = 0,
	threshold = 0.1,
	once = true,
	className,
}: RevealProps) {
	const [ref, isVisible] = useInView<HTMLDivElement>({ threshold, once })

	return (
		<div
			ref={ref}
			data-motion="reveal"
			className={cn(
				'transition-all',
				entrances[animation].timing,
				isVisible ? revealShown : entrances[animation].initial,
				className,
			)}
			style={delay > 0 ? { transitionDelay: `${delay}ms` } : undefined}
		>
			{children}
		</div>
	)
}
